import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { OfflineDetector } from "@/components/OfflineDetector";
import { NotificationPrompt } from "@/components/notifications/NotificationPrompt";

// Critical pages - loaded immediately
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Lazy loaded pages - code split for better performance
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ResumeEditor = lazy(() => import("./pages/ResumeEditor"));
const Settings = lazy(() => import("./pages/Settings"));
const Pricing = lazy(() => import("./pages/Pricing"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const JobTracker = lazy(() => import("./pages/JobTracker"));
const JobBoard = lazy(() => import("./pages/JobBoard"));
const PublicResume = lazy(() => import("./pages/PublicResume"));
const CareerPivot = lazy(() => import("./pages/CareerPivot"));
const InterviewPractice = lazy(() => import("./pages/InterviewPractice"));
const Install = lazy(() => import("./pages/Install"));
const Offline = lazy(() => import("./pages/Offline"));
const Welcome = lazy(() => import("./pages/Welcome"));

// Blog pages - lazy loaded
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const ATSFriendlyResumeFormat = lazy(() => import("./pages/blog/ATSFriendlyResumeFormat"));
const ResumeExamplesByIndustry = lazy(() => import("./pages/blog/ResumeExamplesByIndustry"));
const HowToWriteResume2026 = lazy(() => import("./pages/blog/HowToWriteResume2026"));
const CoverLetterGuide = lazy(() => import("./pages/blog/CoverLetterGuide"));
const ATSTips = lazy(() => import("./pages/blog/ATSTips"));
const ATSResumeOptimization = lazy(() => import("./pages/blog/ATSResumeOptimization"));
const FresherResumeGuide = lazy(() => import("./pages/blog/FresherResumeGuide"));
const InterviewPreparationGuide = lazy(() => import("./pages/blog/InterviewPreparationGuide"));
const WhatIsATS = lazy(() => import("./pages/blog/WhatIsATS"));
const CheckResumeATSScore = lazy(() => import("./pages/blog/CheckResumeATSScore"));
const ResumeRejectionReasons = lazy(() => import("./pages/blog/ResumeRejectionReasons"));
const TailorResumeToJobDescription = lazy(() => import("./pages/blog/TailorResumeToJobDescription"));
const ResumeActionVerbs = lazy(() => import("./pages/blog/ResumeActionVerbs"));
const ResumeIsDead = lazy(() => import("./pages/blog/ResumeIsDead"));

// Company pages - lazy loaded
const About = lazy(() => import("./pages/About"));
const Careers = lazy(() => import("./pages/Careers"));
const Contact = lazy(() => import("./pages/Contact"));

// Product/Feature pages - lazy loaded
const Templates = lazy(() => import("./pages/Templates"));
const AIWriter = lazy(() => import("./pages/features/AIWriter"));
const ATSOptimizer = lazy(() => import("./pages/features/ATSOptimizer"));
const ATSScoreChecker = lazy(() => import("./pages/features/ATSScoreChecker"));
const ResumeBuilder = lazy(() => import("./pages/features/ResumeBuilder"));
const CoverLetterBuilder = lazy(() => import("./pages/features/CoverLetterBuilder"));
const ResumeExamples = lazy(() => import("./pages/features/ResumeExamples"));
const Features = lazy(() => import("./pages/Features"));
const Resources = lazy(() => import("./pages/Resources"));
const FreeResumeBuilder = lazy(() => import("./pages/features/FreeResumeBuilder"));
const ResumeBuilderForStudents = lazy(() => import("./pages/features/ResumeBuilderForStudents"));
const AIResumeBuilder = lazy(() => import("./pages/features/AIResumeBuilder"));
const OnlineResumeMaker = lazy(() => import("./pages/features/OnlineResumeMaker"));
const AICVWalaTemplates = lazy(() => import("./pages/AICVWalaTemplates"));
const AICVWalaReview = lazy(() => import("./pages/AICVWalaReview"));

// Legal/Resource pages - lazy loaded
const Help = lazy(() => import("./pages/Help"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <OfflineDetector />
              <InstallPrompt />
              <NotificationPrompt />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/install" element={<Install />} />
                  <Route path="/offline" element={<Offline />} />
                  <Route path="/welcome" element={<Welcome />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/blog" element={<BlogIndex />} />
                  <Route path="/blog/ats-friendly-resume-format" element={<ATSFriendlyResumeFormat />} />
                  <Route path="/blog/resume-examples-by-industry" element={<ResumeExamplesByIndustry />} />
                  <Route path="/blog/how-to-write-resume-2026" element={<HowToWriteResume2026 />} />
                  <Route path="/blog/cover-letter-guide" element={<CoverLetterGuide />} />
                  <Route path="/blog/ats-tips" element={<ATSTips />} />
                  <Route path="/blog/ats-resume-optimization" element={<ATSResumeOptimization />} />
                  <Route path="/blog/fresher-resume-guide" element={<FresherResumeGuide />} />
                  <Route path="/blog/interview-preparation-guide" element={<InterviewPreparationGuide />} />
                  <Route path="/blog/what-is-ats" element={<WhatIsATS />} />
                  <Route path="/blog/check-resume-ats-score" element={<CheckResumeATSScore />} />
                  <Route path="/blog/resume-rejection-reasons" element={<ResumeRejectionReasons />} />
                  <Route path="/blog/tailor-resume-to-job-description" element={<TailorResumeToJobDescription />} />
                  <Route path="/blog/resume-action-verbs" element={<ResumeActionVerbs />} />
                  <Route path="/blog/resume-is-dead" element={<ResumeIsDead />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/careers" element={<Careers />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/templates" element={<Templates />} />
                  <Route path="/features" element={<Features />} />
                  <Route path="/resources" element={<Resources />} />
                  <Route path="/features/ai-writer" element={<AIWriter />} />
                  <Route path="/features/ats-optimizer" element={<ATSOptimizer />} />
                  <Route path="/ats-score-checker" element={<ATSScoreChecker />} />
                  <Route path="/features/resume-builder" element={<ResumeBuilder />} />
                  <Route path="/features/cover-letter-builder" element={<CoverLetterBuilder />} />
                  <Route path="/features/resume-examples" element={<ResumeExamples />} />
                  <Route path="/features/free-resume-builder" element={<FreeResumeBuilder />} />
                  <Route path="/features/resume-builder-for-students" element={<ResumeBuilderForStudents />} />
                  <Route path="/features/ai-resume-builder" element={<AIResumeBuilder />} />
                  <Route path="/features/online-resume-maker" element={<OnlineResumeMaker />} />
                  <Route path="/aicvwala-templates" element={<AICVWalaTemplates />} />
                  <Route path="/aicvwala-review" element={<AICVWalaReview />} />
                  <Route path="/help" element={<Help />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/cookies" element={<Cookies />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/editor/:id?"
                    element={
                      <ProtectedRoute>
                        <ResumeEditor />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Settings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/job-tracker"
                    element={
                      <ProtectedRoute>
                        <JobTracker />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/career-pivot"
                    element={
                      <ProtectedRoute>
                        <CareerPivot />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/interview-practice"
                    element={
                      <ProtectedRoute>
                        <InterviewPractice />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/jobs"
                    element={
                      <ProtectedRoute>
                        <JobBoard />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/r/:slug" element={<PublicResume />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
